import { useNavigate } from 'react-router-dom';
import { KPICard } from '@/components/data-display';
import type { DashboardKPIData } from './useDashboard';

// ---------------------------------------------------------------------------
// Skeleton loader for KPI cards during initial load
// ---------------------------------------------------------------------------

function KPICardSkeleton() {
  return (
    <div
      className="animate-pulse rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white px-6 py-7 text-center shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
      role="status"
      aria-label="Loading KPI"
    >
      <div className="mx-auto mb-3 h-7 w-7 rounded-full bg-[#f0f0f0]" />
      <div className="mx-auto mb-2 h-8 w-20 rounded bg-[#f0f0f0]" />
      <div className="mx-auto h-3 w-24 rounded bg-[#f0f0f0]" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI card configuration — matches legacy dashboard exactly
// ---------------------------------------------------------------------------

function safeNumber(v: string | number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

interface KPIConfig {
  key: keyof DashboardKPIData;
  label: string;
  icon: string;
  format: (value: string | number) => string;
  /** Destination route when the card is clicked. Omit to keep the card non-interactive. */
  route?: string;
}

const KPI_CONFIGS: KPIConfig[] = [
  {
    key: 'active_clients',
    label: 'Active Clients',
    icon: '👥', // 👥
    format: (v) => safeNumber(v).toLocaleString('en-US'),
    route: '/clients',
  },
  {
    key: 'pending_instructions',
    label: 'Pending Payments',
    icon: '📋', // 📋
    format: (v) => safeNumber(v).toLocaleString('en-US'),
    route: '/monitoring',
  },
  {
    key: 'fx_trades_inflight',
    label: 'FX Trades',
    icon: '💱', // 💱
    format: (v) => safeNumber(v).toLocaleString('en-US'),
  },
  {
    key: 'total_revenue_ytd',
    label: 'Total Revenue',
    icon: '💰', // 💰
    format: (v) =>
      '$' +
      safeNumber(v).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    route: '/revenue',
  },
  {
    key: 'incoming_pending',
    label: 'Incoming Payments',
    icon: '📥', // 📥
    format: (v) => safeNumber(v).toLocaleString('en-US'),
    route: '/incoming',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DashboardKPIsProps {
  data: DashboardKPIData | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function DashboardKPIs({ data, isLoading, error }: DashboardKPIsProps) {
  const navigate = useNavigate();

  if (error && !data) {
    return (
      <div
        className="rounded-lg border border-[#c62828]/20 bg-[#c62828]/5 p-4 text-sm text-[#c62828]"
        role="alert"
      >
        Failed to load KPIs. Please try again.
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      aria-label="Key performance indicators"
    >
      {isLoading && !data
        ? KPI_CONFIGS.map((cfg) => <KPICardSkeleton key={cfg.key} />)
        : KPI_CONFIGS.map((cfg) => (
            <div key={cfg.key} className="relative">
              {/* pointer-events-none so the icon doesn't swallow clicks that
                  should reach the underlying KPICard button. */}
              <span
                className="pointer-events-none absolute left-4 top-4 text-[28px]"
                aria-hidden="true"
              >
                {cfg.icon}
              </span>
              <KPICard
                value={data ? cfg.format(data[cfg.key]) : '—'}
                label={cfg.label}
                className="pt-14"
                onClick={cfg.route ? () => navigate(cfg.route!) : undefined}
                aria-label={cfg.route ? `View ${cfg.label}` : undefined}
              />
            </div>
          ))}
    </div>
  );
}
